<template>
  <div class="stock-match-container">
    <modal
      v-if="showStockModal"
      max-width="100%"
      :full-page="true"
      :title="modalTitle"
      class="cars-list-modal"
      @close="toggleStockModal(false)"
    >
      <div v-if="!isLoading && showPagination" class="stock-toolbar">
        <div class="pagination-control">
          <button
            type="button"
            class="page-btn"
            aria-label="Vorige"
            :disabled="!canPagePrev"
            @click="pagePrev"
          >
            &#x276E;
          </button>
          <span class="page-counter"
            >{{ pageRangeStart }}&ndash;{{ pageRangeEnd }} / {{ stockMatchCars.length }}</span
          >
          <button
            type="button"
            class="page-btn"
            aria-label="Volgende"
            :disabled="!canPageNext"
            @click="pageNext"
          >
            &#x276F;
          </button>
        </div>
      </div>
      <div class="modal-layout">
        <aside class="match-filters">
          <h4>{{ i18n.t('/stockmatch/essentialmatchlabel') }}</h4>
          <form-toggle
            id="filter-drive"
            v-model="filterDrive"
            :title="i18n.t('/stockmatch/filterdrivelabel')"
            @input="fetchStock"
          />
          <form-toggle
            id="filter-color"
            v-model="filterColor"
            :title="i18n.t('/stockmatch/filtercolorlabel')"
            @input="fetchStock"
          />
          <form-toggle
            id="filter-interior"
            v-model="filterInterior"
            :title="i18n.t('/stockmatch/filterinteriorlabel')"
            @input="fetchStock"
          />
          <!-- TODO CST-4252: need to add rim selection -->
          <!-- <form-toggle
            id="filter-wheels"
            v-model="filterWheels"
            :title="i18n.t('/stockmatch/filterwheelslabel')"
            @input="fetchStock"
          /> -->
          <h4 v-if="configOptions.length" class="options-heading">
            {{ i18n.t('/stockmatch/filteroptionslabel') }}
          </h4>
          <form-toggle
            v-for="option in configOptions"
            :id="`filter-option-${option.shortId}`"
            :key="option.shortId"
            v-model="filterOptions[option.shortId]"
            :title="`${option.shortId} · ${option.name}`"
            @input="fetchStock"
          />
          <ds-button
            variant="ghost"
            size="sm"
            block
            class="clear-filters"
            data-qa="clear-stock-filters"
            :disabled="!hasActiveFilters"
            @click="clearFiltersAndFetch"
          >
            {{ i18n.t('/stockmatch/clearfilters') }}
          </ds-button>
        </aside>
        <div ref="viewport" class="matching-cars-viewport">
          <loader v-if="isLoading" type="alert" />
          <template v-else>
            <div class="car-column config-column">
              <stock-match-card
                reference
                :title="i18n.t('/stockmatch/referencetitle')"
                :image-url="images.exterior || undefined"
                :display-name="configuredCarTitle"
                :price="parseFloat(price.Incl)"
                :engine="configuredCarEngine"
                :rows="referenceRows"
              />
            </div>
            <ul v-if="stockMatchCars.length" class="matching-cars">
              <li
                v-for="(car, index) in visibleCars"
                :key="car.CommissionNumber"
                class="car-column"
              >
                <stock-match-card
                  :title="i18n.t('/stockmatch/matchtitle', { number: pageStart + index + 1 })"
                  :image-url="car.ImageExterior || undefined"
                  :display-name="`${configuration.ModelName} ${car.Trimline}`"
                  :price="parseFloat(car.ConsumerPrice)"
                  :price-difference="parseFloat(car.ConsumerPrice) - parseFloat(price.Incl)"
                  :engine="configuredCarEngine"
                  :origin="stockOrigin(car)"
                  :kom-number="car.CommissionNumber"
                  :rows="stockMatchRows(car)"
                  :extras="stockMatchExtras(car)"
                  @stockcar-details="setStockCarDetails(car.CommissionNumber, car)"
                  @reservation-status="setReservationStatusId(car.CommissionNumber)"
                  @select-car="showMatchingStockCarToNewQuotePrompt(car)"
                />
              </li>
            </ul>
            <p v-else class="no-stock-message">
              {{ i18n.t('/stockmatch/nomatchestext') }}
            </p>
          </template>
        </div>
      </div>
    </modal>

    <stock-match-comparison
      v-if="stockCarDetails && stockCarForComparison"
      :config-title="configuredCarTitle"
      :config-price="parseFloat(price.Incl)"
      :configuration-code="configurationCode"
      :config-images="images"
      :engine="configuredCarEngine"
      :reference-rows="referenceRows"
      :stock-title="`${configuration.ModelName} ${stockCarForComparison.Trimline}`"
      :stock-price="parseFloat(stockCarForComparison.ConsumerPrice)"
      :origin="stockOrigin(stockCarForComparison)"
      :kom-number="stockCarForComparison.CommissionNumber"
      :status="stockCarStatus(stockCarForComparison)"
      :stock-images="stockCarDetails.Configuration?.Images || {}"
      :rows="stockMatchRows(stockCarForComparison)"
      :extras="stockMatchExtras(stockCarForComparison)"
      @close="setStockCarDetails(null)"
      @stock-quote="showMatchingStockCarToNewQuotePrompt(stockCarForComparison)"
      @reservation-status="setReservationStatusId(stockCarForComparison.CommissionNumber)"
    />

    <matching-stock-car-prompt
      :show="showMatchingStockCarPrompt"
      @close="closeMatchingStockCarToNewQuotePrompt()"
      @confirm="applyToNewQuote"
    />

    <modal
      v-if="stockCarForReservationStatus && dealerSettings.newStockFlowEnabled"
      id="stock-car-reservation-status"
      :z-index="400"
      :title="i18n.t('/StockDashboard/ReservationStatus/Title')"
      @close="setReservationStatusId(null)"
    >
      <stock-reservation-status-new :stock-car="stockCarForReservationStatus" />
    </modal>
    <modal
      v-else-if="stockCarForReservationStatus && !dealerSettings.newStockFlowEnabled"
      id="stock-car-reservation-status"
      :z-index="400"
      :title="i18n.t('/StockDashboard/ReservationStatus/Title')"
      @close="setReservationStatusId(null)"
    >
      <stock-reservation-status :stock-car-id="stockCarForReservationStatus.Id" />
    </modal>
  </div>
</template>

<script>
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import FormToggle from '@/components/generic/presentational/atoms/formCheckbox/FormToggle.vue'
import StockMatchCard from '@/components/generic/presentational/molecules/stockMatchCard/StockMatchCard.vue'
import DsButton from '@/components/ds/atoms/dsButton/DsButton.vue'
import MatchingStockCarPrompt from '@/components/generic/presentational/molecules/matchingStockCarPrompt/MatchingStockCarPrompt.vue'
import { mapMutations, mapState, mapActions } from 'vuex'
import StockMatching from '@/api/server/StockMatching'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import StockMatchComparison from '@/components/generic/presentational/molecules/stockMatchComparison/StockMatchComparison.vue'
import StockReservationStatusNew from '@/components/generic/presentational/organisms/stockDashboardNew/StockReservationStatusNew.vue'
import StockReservationStatus from '@/components/generic/presentational/molecules/stockReservationStatus/StockReservationStatus.vue'
import StockCarsNewApi from '@/api/server/StockCarsNew'
import stockCarsService from '@/services/stockCarsService'
import moment from 'moment'
import StockMatchOptions from '@/mixins/stockMatchOptions'

export default {
  components: {
    Modal,
    FormToggle,
    StockMatchCard,
    DsButton,
    StockMatchComparison,
    Loader,
    StockReservationStatusNew,
    StockReservationStatus,
    MatchingStockCarPrompt,
  },
  mixins: [StockMatchOptions],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showStockModal: false,
      filterDrive: false,
      filterColor: false,
      filterInterior: false,
      // TODO CST-4252: need to add rim selection
      // filterWheels: false,
      filterOptions: {},
      isLoading: true,
      stockCarDetails: null,
      stockCarForReservationStatus: null,
      showMatchingStockCarPrompt: false,
      selectedMatchedStockCar: null,
      stockCarForComparison: null,
      pageStart: 0,
      pageSize: 4,
    }
  },
  computed: {
    ...mapState('stockcars', ['matchingStockCars']),
    ...mapState('quote', ['quote']),
    ...mapState('configuration', ['configurationCode', 'configuration', 'price', 'images']),
    ...mapState('user', ['dealerSettings']),
    configuredCarTitle() {
      return `${this.configuration.ModelName} ${this.configuration.TrimlineName || ''}`
    },
    hasActiveFilters() {
      return (
        this.filterDrive ||
        this.filterColor ||
        this.filterInterior ||
        Object.values(this.filterOptions).some(Boolean)
      )
    },
    modalTitle() {
      return `${this.i18n.t('/stockmatch/modaltitle')} (${this.stockMatchCars.length})`
    },
    // The matching stock cars currently visible in the paginated window.
    visibleCars() {
      return this.stockMatchCars.slice(this.pageStart, this.pageStart + this.pageSize)
    },
    // Only show the pager when there is more than one page to navigate.
    showPagination() {
      return this.stockMatchCars.length > this.pageSize
    },
    canPagePrev() {
      return this.pageStart > 0
    },
    canPageNext() {
      return this.pageStart + this.pageSize < this.stockMatchCars.length
    },
    pageRangeStart() {
      return this.stockMatchCars.length ? this.pageStart + 1 : 0
    },
    pageRangeEnd() {
      return Math.min(this.pageStart + this.pageSize, this.stockMatchCars.length)
    },
  },
  watch: {
    configurationCode() {
      this.clearFilters()
      this.clearMatchedStockCars()
      this.fetchStock()
    },
    stockMatchCars() {
      // Reset to the first page whenever the (filtered) result set changes.
      this.pageStart = 0
      this.$nextTick(this.updatePageSize)
    },
  },
  mounted() {
    this.fetchStock()
    window.addEventListener('resize', this.updatePageSize)
  },
  unmounted() {
    this.clearFilters()
    this.clearMatchedStockCars()
    window.removeEventListener('resize', this.updatePageSize)
  },
  methods: {
    ...mapMutations({
      setMatchingStockCars: 'stockcars/setMatchingStockCars',
      setShouldCreateMatchingStockQuote: 'quote/setShouldCreateMatchingStockQuote',
      setPrevQuote: 'quote/setPrevQuote',
      setCreateMatchingStockQuoteOptions: 'quote/setCreateMatchingStockQuoteOptions',
    }),
    ...mapActions({
      setLoading: 'quote/setLoading',
      setStockButtonDate: 'quote/setStockButtonDate',
    }),
    clearMatchedStockCars() {
      this.isLoading = true
      this.setMatchingStockCars({})
    },
    clearFilters() {
      this.filterDrive = false
      this.filterColor = false
      this.filterInterior = false
      // TODO CST-4252: need to add rim selection
      // this.filterWheels = false
      this.filterOptions = {}
    },
    clearFiltersAndFetch() {
      this.clearFilters()
      this.fetchStock()
    },
    async fetchStock() {
      if (!this.configurationCode) return

      const options = []
      for (const [optId, checked] of Object.entries(this.filterOptions)) {
        if (checked) options.push(optId)
      }
      try {
        const matchingStock = await StockMatching.getMatchingStockCars(this.configurationCode, {
          engine: this.filterDrive,
          transmission: this.filterDrive,
          interior: this.filterInterior,
          color: this.filterColor,
          options,
        })
        this.setMatchingStockCars(matchingStock)
        this.isLoading = false
      } catch (e) {}
    },
    toggleStockModal(show) {
      this.showStockModal = show
      if (show) {
        let dateNow = moment.utc().format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS)
        this.setStockButtonDate(dateNow)
      }

      if (show && this.isLoading) {
        this.fetchStock()
      }

      if (show) {
        this.$nextTick(this.updatePageSize)
      }
    },
    // How many stock-car columns fit next to the frozen config column.
    // Horizontal navigation is pagination — there is no native horizontal scroll.
    updatePageSize() {
      const el = this.$refs.viewport
      if (!el) {
        this.pageSize = 4
        return
      }
      const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
      // A `.car-column` is `--column-width + 1rem` wide with a `0.5rem` gap.
      const columnWidth = parseFloat(getComputedStyle(el).getPropertyValue('--column-width')) || 240
      const cardStep = columnWidth + remPx + remPx * 0.5
      // The config column takes one slot (same width + its `0.5rem` right margin).
      const configWidth = columnWidth + remPx + remPx * 0.5
      const available = el.clientWidth - configWidth
      this.pageSize = Math.max(1, Math.floor(available / cardStep))
      if (this.pageStart >= this.stockMatchCars.length) this.pageStart = 0
    },
    pagePrev() {
      this.pageStart = Math.max(0, this.pageStart - this.pageSize)
    },
    pageNext() {
      if (this.canPageNext) this.pageStart += this.pageSize
    },
    stockCarStatus(car) {
      return car.CarStatus ? this.i18n.t(`/stockmatch/carstatus/${car.CarStatus}`) : ''
    },
    showMatchingStockCarToNewQuotePrompt(car) {
      this.selectedMatchedStockCar = car
      this.showMatchingStockCarPrompt = true
    },
    closeMatchingStockCarToNewQuotePrompt() {
      this.selectedMatchedStockCar = null
      this.showMatchingStockCarPrompt = false
    },
    applyToNewQuote(options) {
      let car = this.selectedMatchedStockCar
      this.setPrevQuote(this.quote)
      this.setLoading(true)
      this.setShouldCreateMatchingStockQuote(true)
      this.setCreateMatchingStockQuoteOptions(options)
      this.clearMatchedStockCars()
      this.$router.push({
        name: 'new-stockcar-komnumber-page',
        params: {
          komnummer: car.CommissionNumber,
          cstType: this.$route.params.cstType,
        },
      })
    },
    async fetchStockCarByCommissionNumber(commissionNumber) {
      if (this.dealerSettings.newStockFlowEnabled) {
        const results = await StockCarsNewApi.getStockcarsNew({
          search: commissionNumber,
          size: 1,
          page: 1,
          includeNationWide: true,
        })
        return results?.[0] ?? null
      }
      return await stockCarsService.getStockCarByKomnumber(commissionNumber)
    },
    async setStockCarDetails(commissionNumber, matchingCar = null) {
      this.stockCarDetails = commissionNumber
        ? await this.fetchStockCarByCommissionNumber(commissionNumber)
        : null
      this.stockCarForComparison = commissionNumber ? matchingCar : null
    },
    async setReservationStatusId(commissionNumber) {
      this.stockCarForReservationStatus = commissionNumber
        ? await this.fetchStockCarByCommissionNumber(commissionNumber)
        : null
    },
  },
}
</script>

<style lang="scss" scoped>
$stock-navy: #14233d; //Used for the divider

.stock-match-container {
  display: contents;
}

.cars-list-modal {
  --column-width: 260px;
  font-size: $font-size-base;
  line-height: $line-height-base;
}

.modal-layout {
  display: flex;
  gap: 2rem;
  padding: 0.5rem 0;
}

.match-filters {
  flex-shrink: 0;
  width: 10rem;

  h4 {
    font-weight: $font-weight-bold;
    font-size: $font-size-base;
    margin: 0 0 0.75rem;
  }

  .options-heading {
    margin-top: 1.5rem;
  }

  .clear-filters {
    margin-top: 1.5rem;
  }

  :deep(label) {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

// Keep 3: pagination control, right-aligned at the top of the modal content,
// on the same band as the "Vergelijkbare voorraadauto's (N)" title.
.stock-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.5rem;
}

.pagination-control {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.page-counter {
  min-width: 5rem;
  text-align: center;
  font-size: 0.9rem;
  font-weight: $font-weight-bold;
  color: $gray-darker;
}

.page-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  border: 1px solid $gray-base;
  background: $white;
  color: $gray-darker;
  font-size: 0.9rem;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: $gray-lightest;
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
}

.matching-cars-viewport {
  flex: 1;
  display: flex;
  align-items: flex-start;
  min-width: 0;
  // Keep 4: only the option rows scroll vertically; each card's header is
  // pinned via `position: sticky; top: 0`. Horizontal navigation is enabled
  // via the pagination control, but we allow overflow-x: auto to enable
  // native horizontal scrolling if content exceeds width.
  overflow-x: hidden;
  overflow-y: auto;
  max-height: 75vh;
}

// Keep 4: activate the sticky card header only within this modal's
// vertically-scrolling viewport, so inline usages of the cards stay unaffected.
.matching-cars-viewport :deep(.card-header) {
  position: sticky;
  top: 0;
  z-index: 2;
  background: $white;
}

.config-column {
  width: calc(var(--column-width) + 1rem);
  flex-shrink: 0;
  margin-right: 0.5rem;
  padding-right: 0.5rem;
  // Original req 1: freeze the customer's configured car at the left with an
  // opaque background so scrolling content never shows through.
  position: sticky;
  left: 0;
  z-index: 2;
  background: $white;
  // Keep 2: a solid dark divider (replaces the earlier soft box-shadow).
  border-right: 2px solid $stock-navy;
}

.matching-cars {
  list-style: none;
  display: flex;
  gap: 0.5rem;
  padding: 0;
  margin: 0;
}

.car-column {
  width: calc(var(--column-width) + 1rem);
  flex-shrink: 0;
}

.no-stock-message {
  align-self: center;
  font-weight: bold;
}
</style>
